import type React from 'react';
import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { Combobox, Form, FormField, FormHelp, Icon } from '@zillow/constellation-10';
import { IconSearchFilled } from '@zillow/constellation-icons';
import { logError } from '@zg-rentals/log-error';
import type { AutocompleteAreaApiType } from 'app/shared/types/ApiResponseTypes';
import AutocompleteArea from 'app/shared/models/AutocompleteArea';
import api from 'app/shared/utils/api';
import { useAppDispatch } from 'app/shared/flux/hooks';
import debounce from 'lodash/debounce';

import type { ComboboxOptionType } from '@zillow/constellation-10';
import type { State } from 'app/shared/types/State';

export type AutocompleteAreaInputType = 'primary' | 'secondary';

interface AutocompleteAreaInputProps {
  submit: (area: AutocompleteArea) => void;
  blockedAreas?: Array<string>;
  type?: AutocompleteAreaInputType;
  error?: string | null;
  placeholder?: string;
}

const AutocompleteAreaInput: React.FunctionComponent<
  React.PropsWithChildren<React.PropsWithChildren<AutocompleteAreaInputProps>>
> = (props) => {
  const { submit, type = 'primary', error, blockedAreas = [], placeholder = 'Search by city or ZIP' } = props;
  const dispatch = useAppDispatch();
  const isMobile = useSelector((state: State) => state.app.device.isMobile);
  const [selectedIdx, setSelectedIdx] = useState<number>(-1);
  const [options, setOptions] = useState<Array<AutocompleteArea>>([]);
  const [inputValue, setInputValue] = useState<string>('');
  const updateSuggestions = useCallback(
    debounce(async (searchTerm: string) => {
      try {
        // @ts-ignore
        const { data, errors } = await dispatch(api.area.autocomplete(searchTerm));
        if (errors.length > 0) {
          throw new Error(errors[0].code);
        }
        const areas: Array<AutocompleteArea> = data
          .map((obj: AutocompleteAreaApiType) => new AutocompleteArea(obj))
          .filter((area: AutocompleteArea) => !blockedAreas.includes(area.resourceId as string));
        setSelectedIdx(areas.length > 0 ? 0 : -1);
        setOptions(areas);
      } catch (e) {
        logError({
          error: e,
          errorType: 'AutocompleteAreaInput',
          context: {
            details: 'AutocompleteAreaInput: Error updating suggestions',
            root: 'components/AutocompleteAreaInput#updateSuggestions',
          },
        });
      }
    }, 200),
    [dispatch, setSelectedIdx, setOptions],
  );
  const onSubmit = useCallback(
    (e: React.FormEvent | React.MouseEvent) => {
      e.preventDefault();
      if (isOptionSelected) {
        submit(options[selectedIdx]);
      }
      e.stopPropagation(); // make sure listbox doesnt reappear
    },
    [submit, options, selectedIdx],
  );

  const isOptionSelected = selectedIdx !== -1;

  return (
    <Form onSubmit={onSubmit} css={{ maxWidth: '485px', flexBasis: '485px' }}>
      <FormField error={!!error} description={!!error && <FormHelp>{error}</FormHelp>}>
        <Combobox
          css={{ width: '100%', backgroundColor: type === 'secondary' ? 'bg.neutral' : undefined }}
          autoCompleteBehavior="none"
          allowSubmitWhileOpen={true}
          focusFirstOption={true}
          placeholder={placeholder}
          options={options.map((area) => area.longName).filter((suggestion) => suggestion !== null) as Array<string>}
          selectCallbackFrequency="always"
          onOptionSelect={(option: ComboboxOptionType) => {
            const val = typeof option === 'string' ? option : (option?.value ?? option);
            const idx = options.findIndex((m) => m.longName === val);
            if (idx !== -1) {
              submit(options[idx]);
            }
          }}
          size={isMobile ? 'sm' : 'md'}
          renderInput={({ ref, ...rest }) => (
            <Combobox.Input
              {...rest}
              value={inputValue}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const newValue = e.target.value;
                setInputValue(newValue);
                if (newValue.length >= 3) {
                  if (!isOptionSelected || (isOptionSelected && newValue !== options[selectedIdx].longName)) {
                    updateSuggestions(newValue);
                  }
                }
              }}
            />
          )}
          renderAdornment={({ ref, ...adornmentProps }) => (
            <Combobox.Adornment {...adornmentProps} id="search-adornment" onClick={onSubmit}>
              <Icon
                css={{
                  color: type === 'primary' ? 'icon.accent.brand.hero !important' : undefined,
                  cursor: 'pointer',
                }}
              >
                <IconSearchFilled />
              </Icon>
            </Combobox.Adornment>
          )}
        />
      </FormField>
    </Form>
  );
};

export default AutocompleteAreaInput;
